import React, { createContext, useReducer, useContext, useEffect } from 'react';
import api from '../api/axios';
import i18n from '../i18n';

const PERSIST_LANG_KEY = 'lang';
const persistLang = (lang) => { if (lang) localStorage.setItem(PERSIST_LANG_KEY, lang); };
const applyPersistedLang = (lang) => { if (lang && i18n?.changeLanguage && i18n.language !== lang) i18n.changeLanguage(lang); };

// 1. Action Types
const LOGIN_SUCCESS = 'LOGIN_SUCCESS';
const USER_LOADED = 'USER_LOADED';
const AUTH_ERROR = 'AUTH_ERROR';
const LOGIN_FAIL = 'LOGIN_FAIL';
const LOGOUT = 'LOGOUT';

// 2. Initial State
const initialState = {
  token: localStorage.getItem('token'),
  isAuthenticated: null,
  loading: true,
  user: null
};

// 3. Reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case USER_LOADED:
      return {
        ...state,
        isAuthenticated: true,
        loading: false,
        user: action.payload // payload contains the user object with 'role'
      };
    case LOGIN_SUCCESS:
      localStorage.setItem('token', action.payload.token);
      return {
        ...state,
        token: action.payload.token,
        isAuthenticated: true,
        loading: false,
        // Spread user data from login response if available
        user: action.payload.user || null 
      };
    case AUTH_ERROR:
    case LOGIN_FAIL:
    case LOGOUT:
      localStorage.removeItem('token');
      return {
        ...state,
        token: null,
        isAuthenticated: false,
        loading: false,
        user: null
      };
    default:
      return state;
  }
};

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const { isAuthenticated, loading, user, token } = state;

  // --- ACTION: LOAD USER ---
  const loadUser = async () => {
    // If no token exists, don't even try the request
    if (!localStorage.getItem('token')) {
      dispatch({ type: AUTH_ERROR });
      return;
    }

    try {
      // The interceptor automatically attaches the token to this request
      const res = await api.get('/auth/me');

      dispatch({
        type: USER_LOADED,
        payload: res.data // Backend returns the User object
      });

      const lang = res.data.language || res.data.lang;
      if (lang) { persistLang(lang); applyPersistedLang(lang); }
      
    } catch (err) {
      console.error("Load User Error:", err.response?.data || err.message);
      dispatch({ type: AUTH_ERROR });
    }
  };

  // --- ACTION: LOGIN ---
  const login = async (email, password) => {
    try {
      // Cleaner call: no need for JSON.stringify or manual config
      const res = await api.post('/auth/login', { email, password });

      // 1. Save Token immediately (so interceptor can use it for next calls)
      localStorage.setItem('token', res.data.token);

      // 2. Handle Language
      const lang = res.data.user?.language || res.data.user?.lang;
      if (lang) { persistLang(lang); applyPersistedLang(lang); }

      // 3. Update state
      dispatch({
        type: LOGIN_SUCCESS,
        payload: res.data // res.data = { token, user: { ... } }
      });

      // 4. Refresh user data to ensure all fields (like favorites) are loaded
      await loadUser();

    } catch (err) {
      dispatch({ type: LOGIN_FAIL });
      throw err; // Allow Login component to show error message
    }
  };

  const logout = () => dispatch({ type: LOGOUT });

  // ON START: Persistence check
  useEffect(() => {
    const storedLang = localStorage.getItem(PERSIST_LANG_KEY);
    if (storedLang) applyPersistedLang(storedLang);

    if (localStorage.token) {
      loadUser();
    } else {
      dispatch({ type: AUTH_ERROR });
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{ token, isAuthenticated, loading, user, login, logout, loadUser }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
